import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, NavigationExtras, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';

export enum ROUTE_NAMES {
	AUTH = 'auth',
	AUTH_LOGIN = 'auth.login',
	AUTH_SIGNUP = 'auth.signup',
	AUTH_FORGOT = 'auth.forgot',
	AUTH_RESET = 'auth.reset',
	AUTH_COURSE_CONFIRM = 'auth.course-confirm',
	DASHBOARD_ROOT = 'root',
	DASHBOARD_FOLDERS = 'root.folders',
	DASHBOARD_NO_GROUPS = 'root.no-groups',
	DASHBOARD_ACTIVITY_VIEW = 'root.activity-view',
	DASHBOARD_ACTIVITY_TASK = 'root.activity-view.task',
	DASHBOARD_ACTIVITY_TASK_REPORT = 'root.activity-view.task-report',
	DASHBOARD_ACTIVITY_REPORT = 'root.activity-view.activity-report',
	DASHBOARD_SESSION_VIEW = 'root.session-view',
	DASHBOARD_FOLDER_VIEW = 'root.folder-view',
	LICENSE_MANAGEMENT = 'root.license-management',
	LICENSE_SEATS_MANAGEMENT = 'root.license-seats-management',
	DASHBOARD_SETTINGS = 'root.settings',
	DASHBOARD_LICENSE_MANAGEMENT_ORG = 'root.license-management-org',
	DASHBOARD_ARCHIVE = 'root.archive',
	DASHBOARD_WIZARD = 'root.wizard',
}

export const ROUTER_CONFIG = {
	[ROUTE_NAMES.AUTH]: {
		path: 'dashboard/auth',
		params: []
	},
	[ROUTE_NAMES.AUTH_LOGIN]: {
		path: 'dashboard/auth/login',
		params: []
	},
	[ROUTE_NAMES.AUTH_SIGNUP]: {
		path: 'dashboard/auth/signup',
		params: []
	},
	[ROUTE_NAMES.AUTH_FORGOT]: {
		path: 'dashboard/auth/forgot',
		params: []
	},
	[ROUTE_NAMES.AUTH_RESET]: {
		path: 'dashboard/auth/reset/:email/:expiry/:hash',
		params: ['email', 'expiry', 'hash']
	},
	[ROUTE_NAMES.AUTH_COURSE_CONFIRM]: {
		path: 'dashboard/auth/group-confirm',
		params: []
	},
	[ROUTE_NAMES.DASHBOARD_ROOT]: {
		path: 'dashboard',
		params: []
	},
	[ROUTE_NAMES.DASHBOARD_FOLDERS]: {
		path: 'dashboard/folders/:id',
		params: ['id']
	},
	[ROUTE_NAMES.DASHBOARD_NO_GROUPS]: {
		path: 'dashboard/no-access',
		params: []
	},
	[ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW]: {
		path: 'dashboard/activities/:activity_id',
		params: ['activity_id']
	},
	[ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK]: {
		path: 'dashboard/activities/:activity_id/tasks/:type',
		params: ['activity_id', 'type']
	},
	[ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT]: {
		path: 'dashboard/activities/:activity_id/task-report',
		params: ['activity_id']
	},
	[ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT]: {
		path: 'dashboard/activities/:activity_id/activity-report',
		params: ['activity_id']
	},
	[ROUTE_NAMES.DASHBOARD_SESSION_VIEW]: {
		path: 'dashboard/sessions/:session_id',
		params: ['session_id']
	},
	[ROUTE_NAMES.DASHBOARD_FOLDER_VIEW]: {
		path: 'dashboard/folder/:folder_id',
		params: ['folder_id']
	},
	[ROUTE_NAMES.DASHBOARD_SETTINGS]: {
		path: 'dashboard/settings',
		params: []
	},
	[ROUTE_NAMES.DASHBOARD_ARCHIVE]: {
		path: 'dashboard/archive',
		params: []
	},
	[ROUTE_NAMES.DASHBOARD_WIZARD]: {
		path: 'dashboard/wizard',
		params: []
	},
	[ROUTE_NAMES.LICENSE_MANAGEMENT]: {
		path: 'dashboard/license-management',
		params: []
	},
	[ROUTE_NAMES.LICENSE_SEATS_MANAGEMENT]: {
		path: 'dashboard/license-management/:licenseId/seats',
		params: ['licenseId']
	},
	[ROUTE_NAMES.DASHBOARD_LICENSE_MANAGEMENT_ORG]: {
		path: 'dashboard/license-management/:orgId',
		params: ['orgId']
	}
} as const;


export function getPathTemplate (routeName: ROUTE_NAMES): string {
	return ROUTER_CONFIG[routeName]?.path || '';
}

@Injectable({ providedIn: 'root' })
export class RouterService {
	constructor (
		private router: Router,
		private route: ActivatedRoute,
		private environmentVarsService: EnvironmentVarsService
	) {}

	public buildUrl (routeName: ROUTE_NAMES, params?: Record<string, any>): any[] {
		const config = ROUTER_CONFIG[routeName];
		if (!config) {
			throw new Error(`Route ${routeName} not found in ROUTER_CONFIG`);
		}

		let pathSegments = config.path.split('/');

		if (params) {
			pathSegments = pathSegments.map((segment) => {
				if (segment.startsWith(':')) {
					const paramName = segment.substring(1);
					const paramValue = params[paramName];
					if (paramValue === undefined) {
						throw new Error(
							`Missing parameter ${paramName} for route ${routeName}`
						);
					}
					return paramValue;
				}
				return segment;
			});
		}

		return pathSegments;
	}

	public navigateTo (
		routeName: ROUTE_NAMES,
		params?: Record<string, any>,
		extras?: NavigationExtras
	) {
		let url = this.buildUrl(routeName, params);
		if (this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI)) {
			url = this.transformCommandsForLti(url);
		}
		return this.router.navigate(url, extras);
	}

	private transformCommandsForLti (commands: any[]): any[] {
		if (commands.length > 0 && commands[0] === 'dashboard') {
			return commands.slice(1);
		}
		return commands;
	}

	public isRouteActive (routeName: ROUTE_NAMES): boolean {
		const currentRouteName = this.getCurrentRouteName();
		return currentRouteName === routeName;
	}

	public getCurrentRouteName (): ROUTE_NAMES | null {
		const routeSnapshot = this.getCurrentRouteSnapshot();
		return routeSnapshot.data.routeName || null;
	}

	public getPathTemplate (routeName: ROUTE_NAMES): string {
		return ROUTER_CONFIG[routeName]?.path || '';
	}

	public getRequiredParams (routeName: ROUTE_NAMES): string[] {
		return [...(ROUTER_CONFIG[routeName]?.params || [])];
	}

	public validateParams (
		routeName: ROUTE_NAMES,
		params?: Record<string, any>
	): boolean {
		const requiredParams = this.getRequiredParams(routeName);
		if (!params) return requiredParams.length === 0;

		return requiredParams.every((param) => params[param] !== undefined);
	}

	public getCurrentParams (): Record<string, any> {
		const routeSnapshot = this.getCurrentRouteSnapshot();
		// Collect params from current route and all parent routes
		let params = {};
		let currentRoute: ActivatedRouteSnapshot | null = routeSnapshot;
		while (currentRoute) {
			params = { ...currentRoute.params, ...params };
			currentRoute = currentRoute.parent;
		}
		return params;
	}

	public getCurrentQueryParams (): Record<string, any> {
		const routeSnapshot = this.getCurrentRouteSnapshot();
		return routeSnapshot.queryParams;
	}

	public getCurrentData (): Record<string, any> {
		const routeSnapshot = this.getCurrentRouteSnapshot();
		return routeSnapshot.data;
	}

	public getCurrentRouteSnapshot (): ActivatedRouteSnapshot {
		let route = this.route.snapshot;
		while (route.firstChild) {
			route = route.firstChild;
		}
		return route;
	}

	public serializeUrl (urlTree: UrlTree): string {
		return this.router.serializeUrl(urlTree);
	}

	public createUrlTree (commands: readonly any[], navigationExtras?: NavigationExtras): UrlTree {
		return this.router.createUrlTree(commands, navigationExtras);
	}
}
